import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import { Row } from '@tanstack/react-table';
import { isEmpty, isNil } from 'lodash';
import * as math from 'mathjs';
import { MutableRefObject, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';
import { ResolvedQuantity } from '../../domain/property-types';
import {
  AreaUnitTypeEnum,
  CalculationTypeInput,
  DatabaseLookupTypeInput,
  LengthUnitTypeEnum,
  OrderColumnDeepFragment,
  PropertySetType,
  QuantityEnum,
  QuantityTypeInput,
  Unit,
  UnitSettingsType,
  VolumeUnitTypeEnum,
} from '../../gql/graphql';
import { usePropertyContext } from '../../hooks/property-resolving';
import {
  Substitution,
  columnIdToScopeVariable,
  getVariableColor,
  parseDerivedFormula,
  stringifyDerivedFormula,
} from '../../utils/formula-utils';
import {
  formatUnit,
  parseUnit,
  unitToUnitType,
  UnitType,
  UnitTypeToUnit,
} from '../../utils/unit-utils';
import DynamicValuePopover from '../project/order-entries-panel/DynamicValuePopover';
import {
  EditableRowsFormValues,
  TableEntry,
} from '../project/order-entries-panel/OrderEntriesPanel';
import PlaceholderText from '../text/PlaceholderText';
import DynamicQuantitiesModal from '../project/order-entries-panel/DynamicQuantitiesModal';
import SparkelIcon from './icon/SparkelIcon';
import { Point } from 'src/domain/geometry/geometric-types';

export type FormulaVariable = {
  variableName: string;
  name: string;
  columnId: string;
  color?: string;
};

type InputFormulaVariable = {
  name: string;
  position: number;
};

export type FormulaColumnKey = `custom.${string}.quantity` | 'quantity';

export default function SparkelFormulaInputGroup<D extends TableEntry>({
  row,
  columnKey,
  projectId,
  propertySets,
  orderColumns,
  availableSubstitutions,
  columnName,
  columnId,
  allowSumOnly,
  projectSettings,
}: {
  row: Row<D>;
  orderColumns: OrderColumnDeepFragment[];
  columnKey: FormulaColumnKey;
  projectId: string;
  propertySets: PropertySetType[];
  availableSubstitutions: Substitution[];
  columnName: string;
  columnId: string;
  allowSumOnly?: boolean;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  };
}): React.ReactElement {
  const { propertyContext } = usePropertyContext(projectId);

  const enableDynamicValuePopover = useFlag('enable-dynamic-value-popover');

  const {
    isOpen: showDerivedPopover,
    onOpen: openDerivedPopover,
    onClose: closeDerivedPopover,
  } = useDisclosure();

  const {
    isOpen: showLinkQuantitiesModal,
    onOpen: onLinkQuantitiesModalOpen,
    onClose: onLinkQuantitiesModalClose,
  } = useDisclosure();

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext<EditableRowsFormValues>();
  const editFormCalculation = watch(`${row.id}.${columnKey}.calculation`);
  const editFormDatabaseLookup = watch(`${row.id}.${columnKey}.databaseLookup`);

  const [showLinkQuantities, setShowLinkQuantities] = useState(true);
  const [variables, setVariables] = useState<InputFormulaVariable[]>([]);
  const [lastKnownCaretPosition, setLastKnownCaretPosition] = useState<
    number | null
  >(null);

  const ref: MutableRefObject<HTMLInputElement | null> =
    useRef<HTMLInputElement>(null);

  const handleUnlink = () => {
    if (columnKey === 'quantity') {
      setValue(`${row.id}.quantity`, {
        type: QuantityEnum.Static,
        value: getValues(`${row.id}.quantity.value`),
      });
    } else {
      setValue(`${row.id}.${columnKey}`, {
        type: QuantityEnum.Static,
        stringValue: getValues(`${row.id}.${columnKey}.stringValue`) as string,
      });
    }
  };

  const formulaWithVariables = (
    derivedFormula: string,
    variablesToInput: InputFormulaVariable[]
  ) => {
    let formula = derivedFormula;
    if (!isEmpty(variablesToInput)) {
      for (const variable of variablesToInput) {
        formula =
          formula.slice(0, variable.position - 1) +
          variable.name +
          formula.slice(variable.position - 1);
      }
    }
    return stringifyDerivedFormula(formula, orderColumns);
  };

  const onVariableClick = (variableName: string, value?: QuantityTypeInput) => {
    const cursorPosition = ref.current?.selectionStart;
    let position;
    if (variables.length > 0 && lastKnownCaretPosition) {
      position = lastKnownCaretPosition;
    } else {
      position = cursorPosition;
    }
    const newVariables = [
      ...variables,
      {
        name: variableName,
        position: position ?? 0,
      },
    ];
    setVariables(newVariables);
    setLastKnownCaretPosition((position ?? 0) + variableName.length);
    const formula = formulaWithVariables(
      value?.derivedFormula ?? '',
      newVariables
    );
    //Have to set here to support submitting directly after variable click
    setValue(`${row.id}.${columnKey}.derivedFormula`, formula);
  };

  const handleCaretPosition = (
    event: React.SyntheticEvent<HTMLInputElement, Event>
  ) => {
    if (lastKnownCaretPosition) {
      const target = event.target as HTMLInputElement;
      target.selectionStart = lastKnownCaretPosition;
      target.selectionEnd = lastKnownCaretPosition;
      setLastKnownCaretPosition(null);
    }
  };

  // eslint-disable-next-line complexity
  const handleDynamicValueSubmit = async (
    calculation: CalculationTypeInput | null,
    resolvedQuantity: ResolvedQuantity | null,
    unitFormatOverride?: UnitSettingsType,
    databaseLookup?: DatabaseLookupTypeInput
  ) => {
    // Handle database lookup
    if (databaseLookup) {
      setValue(`${row.id}.${columnKey}`, {
        type: QuantityEnum.DatabaseLookup,
        databaseLookup,
        derivedFormula: null,
      });
      return;
    }

    setValue(`${row.id}.${columnKey}`, {
      type: QuantityEnum.Dynamic,
      calculation,
      derivedFormula: null,
      unitFormatOverride,
    });

    if (columnKey === 'quantity' && resolvedQuantity) {
      if (resolvedQuantity) {
        let unit = resolvedQuantity.units
          ? parseUnit(resolvedQuantity.units) ?? Unit.Pcs
          : Unit.Pcs;

        if (unitFormatOverride) {
          const unitType = unitToUnitType(formatUnit(unit));
          let unitOverride:
            | LengthUnitTypeEnum
            | AreaUnitTypeEnum
            | VolumeUnitTypeEnum
            | undefined = undefined;

          switch (unitType) {
            case UnitType.Length:
              unitOverride =
                unitFormatOverride.length?.units ?? LengthUnitTypeEnum.Meters;
              unit = UnitTypeToUnit(unitOverride);
              break;
            case UnitType.Area:
              unitOverride =
                unitFormatOverride.area?.units ??
                AreaUnitTypeEnum.MetersSquared;
              unit = UnitTypeToUnit(unitOverride);
              break;
            case UnitType.Volume:
              unitOverride =
                unitFormatOverride.volume?.units ??
                VolumeUnitTypeEnum.MetersCubed;
              unit = UnitTypeToUnit(unitOverride);
              break;
            default:
              break;
          }
        }

        setValue(`${row.id}.unit`, unit);
      }
    }
  };

  const getInputValue = (value?: QuantityTypeInput) => {
    let inputValue: string | undefined = undefined;
    if (value) {
      if (!isNil(value.derivedFormula)) {
        const formula = formulaWithVariables(value.derivedFormula, variables);
        inputValue = '=' + stringifyDerivedFormula(formula, orderColumns);
      } else if (!isNil(value.stringValue)) {
        inputValue = value.stringValue;
      } else if (!isNil(value.value)) {
        inputValue = value.value.toString();
      }
    }
    return inputValue;
  };

  return (
    <InputGroup size="sm">
      <Controller
        name={`${row.id}.${columnKey}`}
        control={control}
        rules={{
          validate: {
            validStatic: (input) => {
              if (
                columnKey !== 'quantity' ||
                input?.type !== QuantityEnum.Static
              ) {
                return;
              }
              if (Number.isNaN(input.value)) {
                return 'Static input must be a number';
              }
            },
            validFormula: (input) => {
              if (input?.type !== QuantityEnum.Derived) {
                return;
              }
              if (
                isNil(input?.derivedFormula) ||
                isEmpty(input.derivedFormula.trim())
              ) {
                return 'Derived formula cannot be empty';
              }
              try {
                math.parse(
                  parseDerivedFormula(input.derivedFormula, orderColumns)
                );
              } catch (err) {
                return "Couldn't parse formula";
              }
            },
          },
        }}
        render={({ field: { onChange, value, onBlur } }) => {
          const inputValue = getInputValue(value);

          const handleChange: React.ChangeEventHandler<
            HTMLInputElement | HTMLSelectElement
          > = (event) => {
            if (event.target.value.startsWith('=')) {
              openDerivedPopover();
              const input: QuantityTypeInput = {
                type: QuantityEnum.Derived,
                derivedFormula: event.target.value.slice(1),
              };
              if (columnKey === 'quantity') {
                input.value = null;
              } else {
                input.stringValue = null;
              }
              onChange(input);
              setVariables([]);
            } else {
              closeDerivedPopover();
              const input: QuantityTypeInput = {
                type: QuantityEnum.Static,
                stringValue: event.target.value,
                derivedFormula: null,
              };
              if (columnKey === 'quantity') {
                input.value = Number(event.target.value);
              }
              onChange(input);
            }
          };

          const columnErrors =
            columnKey === 'quantity'
              ? errors?.[row.id]?.['quantity']
              : errors?.[row.id]?.['custom']?.[columnId]?.quantity;

          return (
            <Popover
              isOpen={showDerivedPopover}
              onClose={closeDerivedPopover}
              autoFocus={false}
              returnFocusOnClose={false}
              isLazy
            >
              {showLinkQuantities && (
                <InputRightElement>
                  <Popover isOpen={showLinkQuantitiesModal} isLazy>
                    <PopoverAnchor>
                      <IconButton
                        onClick={onLinkQuantitiesModalOpen}
                        aria-label={`Link BIM data to ${columnName}`}
                        size="xs"
                        colorScheme="gray"
                        variant={'ghost'}
                        borderRadius={'md'}
                        icon={
                          <SparkelIcon
                            icon={faBolt}
                            color={
                              value?.type === QuantityEnum.Dynamic ||
                              value?.type === QuantityEnum.DatabaseLookup
                                ? 'teal.400'
                                : undefined
                            }
                          />
                        }
                      />
                    </PopoverAnchor>
                    {enableDynamicValuePopover ? (
                      <Portal>
                        <DynamicValuePopover
                          allowSumOnly={allowSumOnly}
                          propertyContext={propertyContext}
                          propertySets={propertySets}
                          defaultCalculation={editFormCalculation}
                          defaultDatabaseLookup={editFormDatabaseLookup}
                          orderColumns={orderColumns}
                          dbIds={
                            row.original.resolvedElementIdentifiersDbid ?? []
                          }
                          onLink={handleDynamicValueSubmit}
                          onUnlink={handleUnlink}
                          onClose={onLinkQuantitiesModalClose}
                          projectSettings={projectSettings}
                        />
                      </Portal>
                    ) : (
                      <DynamicQuantitiesModal
                        isOpen={showLinkQuantitiesModal}
                        onClose={onLinkQuantitiesModalClose}
                        onLink={handleDynamicValueSubmit}
                        onUnlink={handleUnlink}
                        propertyContext={propertyContext}
                        propertySets={propertySets}
                        dbIds={
                          row.original.resolvedElementIdentifiersDbid ?? []
                        }
                        defaultCalculation={editFormCalculation}
                        projectSettings={projectSettings}
                      />
                    )}
                  </Popover>
                </InputRightElement>
              )}
              <PopoverAnchor>
                <Input
                  isDisabled={
                    value?.type === QuantityEnum.Dynamic ||
                    value?.type === QuantityEnum.DatabaseLookup
                  }
                  onFocus={() => {
                    setShowLinkQuantities(false);
                    if (inputValue?.startsWith('=')) {
                      openDerivedPopover();
                    }
                  }}
                  value={inputValue ?? ''}
                  ref={ref}
                  onBlur={() => {
                    closeDerivedPopover();
                    setShowLinkQuantities(true);
                    onBlur();
                  }}
                  onChange={handleChange}
                  onSelect={handleCaretPosition}
                  aria-label={columnName}
                  step="any"
                  isInvalid={!!columnErrors}
                  textAlign={'start'}
                  form="edit-table-entry-form"
                  size="sm"
                  px={1}
                  borderRadius={0}
                  placeholder={columnName}
                />
              </PopoverAnchor>

              <Portal>
                <SparkelFormulaPopoverContent
                  availableSubstitutions={availableSubstitutions}
                  onVariableClick={(variableName) =>
                    onVariableClick(variableName, value)
                  }
                  orderColumns={orderColumns}
                  inputValue={inputValue}
                  errorMessage={columnErrors?.message}
                />
              </Portal>
              {/* Derived-formula popover content remains below */}
            </Popover>
          );
        }}
      ></Controller>
    </InputGroup>
  );
}

export function SparkelFormulaPopoverContent({
  availableSubstitutions,
  onVariableClick,
  orderColumns,
  inputValue,
  errorMessage,
}: {
  availableSubstitutions: Substitution[];
  onVariableClick: (variableName: string) => void;
  orderColumns: OrderColumnDeepFragment[];
  inputValue?: string;
  errorMessage?: string;
}): React.ReactElement {
  const { t } = useTranslation('table');
  return (
    <PopoverContent>
      <PopoverBody>
        {!isEmpty(availableSubstitutions) && (
          <>
            <PlaceholderText display="block">
              {t('formula-popover.title')}
            </PlaceholderText>
            {availableSubstitutions.map(
              ({ name, variableName, columnId }, index) => (
                <Button
                  key={variableName}
                  aria-label={columnId}
                  variant="link"
                  _hover={{
                    textDecoration: 'none',
                    opacity: 0.7,
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseUp={() => {
                    onVariableClick(variableName);
                  }}
                  marginRight={2}
                  marginY={1}
                >
                  <Badge
                    size={'md'}
                    px={2}
                    colorScheme={getVariableColor(index)}
                    textTransform={'none'}
                  >
                    {name}
                  </Badge>
                </Button>
              )
            )}
            <Divider my={2} />
          </>
        )}
        <SparkelFormulaPreview
          formula={inputValue?.slice(1) ?? ''}
          variables={availableSubstitutions.map((e, index) => ({
            variableName: e.variableName,
            name: e.name,
            color: getVariableColor(index),
            columnId: e.columnId,
          }))}
          columns={orderColumns}
        />
        {errorMessage ? (
          <Alert status="error">
            <AlertTitle>{errorMessage}</AlertTitle>
          </Alert>
        ) : null}
      </PopoverBody>
    </PopoverContent>
  );
}

export function SparkelFormulaPreview({
  formula,
  variables,
  columns,
}: {
  formula: string;
  variables: FormulaVariable[];
  columns: OrderColumnDeepFragment[];
}): React.ReactElement {
  const { t } = useTranslation('table');

  const parsedFormula = parseDerivedFormula(formula, columns);

  const scopeVariables: string[] = variables.map((variable) => {
    return columnIdToScopeVariable(variable.columnId);
  });

  const matchScopeVariablesPattern = new RegExp(
    '(' + scopeVariables.join('|') + ')',
    'g'
  );
  const tokens = parsedFormula.split(matchScopeVariablesPattern);

  return (
    <>
      <PlaceholderText display="block">
        {t('formula-popover.preview')}
      </PlaceholderText>
      <Box display="block">
        {!isEmpty(formula.trim()) ? (
          tokens.map((token, index) => {
            const variable = variables.find(
              (e) => columnIdToScopeVariable(e.columnId) === token
            );
            if (variable) {
              return (
                <Badge
                  key={index}
                  borderRadius="3xl"
                  px={2}
                  colorScheme={variable.color}
                  textTransform={'none'}
                >
                  {variable.name}
                </Badge>
              );
            } else {
              return (
                <Text
                  color={'gray.600'}
                  _dark={{ color: 'gray.200' }}
                  key={index}
                  as="span"
                >{`${token} `}</Text>
              );
            }
          })
        ) : (
          <span>&nbsp;</span>
        )}
      </Box>
    </>
  );
}
